/**
 * @file: types.ts
 *
 * Shared shapes for a single chat-room conversation (see `./provider`).
 */

import { chatThreadTitle } from '@euler/app/flows/chat/utils/chatThreadDisplay';
import { ChatContact, ChatThreadItem } from '@euler/model/chat';
import { ChatConversationHandle, ChatConversationPeer } from './handle';

/**
 * What the caller wants to resolve when opening a conversation by id/contact
 * (the pending half of a {@link ConversationIntent}). It's also the query
 * `resolveConversationHandle` consumes.
 */
export type ConversationHandleQuery =
  | { kind: 'room'; roomId: string }
  | { kind: 'contact'; peer: ChatConversationPeer }
  | { kind: 'member'; orgMemberId: number };

/**
 * A request to open a conversation — it expresses WHO / WHICH room, not a bag
 * of loose params:
 *   - `resolved`: the caller already has the handle (a thread tap, a
 *     pre-resolved push) — the common case, no server round-trip,
 *   - `room` / `contact`: pending a resolve on entry (open a known room, or
 *     talk to a contact whose room may not exist yet).
 */
export type ConversationIntent =
  | { kind: 'resolved'; handle: ChatConversationHandle }
  | ConversationHandleQuery;

/**
 * The handle for a tapped thread — already resolved (the row carries the room
 * + thread), no server round-trip. `selfName` is the viewer's name, used to
 * title a 1:1 from the other party.
 */
export function conversationHandleForThread(
  thread: ChatThreadItem,
  selfName: string | null,
): ChatConversationHandle {
  return {
    kind: 'baked',
    roomId: thread.room.id,
    threadId: thread.id,
    roomType: thread.room.type === 'group' ? 'group' : 'private',
    title: chatThreadTitle(thread, selfName),
  };
}

/** Open a tapped thread (for navigation paths that carry an intent param). */
export function conversationIntentForThread(
  thread: ChatThreadItem,
  selfName: string | null,
): ConversationIntent {
  return {
    kind: 'resolved',
    handle: conversationHandleForThread(thread, selfName),
  };
}

/**
 * Open a contact. Its room may not exist yet, so it yields a pending `contact`
 * intent that the surface resolves on entry (or on first send).
 */
export function conversationIntentForContact(
  contact: ChatContact,
): ConversationIntent {
  return {
    kind: 'contact',
    peer: {
      platformUserId: contact.platformUserId,
      appId: contact.appId,
      appScope: contact.appScope,
      name: contact.name,
      avatar: contact.avatar,
    },
  };
}
